import React, { useEffect, useRef } from 'react'
import { connect } from 'react-redux'
import { AuthProvider } from 'contexts/auth'
import { authorizationSelector, Permissions } from 'api'
import { isFeatureActive as _isFeatureActive } from 'api/features'
import { authActions } from 'state/auth'
import { API_ROUND_ENROLMENTS_INTERNAL_SUBMISSIONS_CREATE } from 'state/roundEnrolment/actions'
import { LOGIN_URL } from 'qap/constants'
import getRedirect from 'utilities/getRedirect'

import type { Feature, User, Organisation } from 'types'

type Props = {
  children: any;
  user: User;
  features: Feature[];
  authorization: any;
  canSwitchOrganisations: boolean;
  organisations: Organisation[];
  currentlyEnrolled: boolean;
  participantTypes: string[];
  submitting: boolean;
  clientSettings: any;
  loadingSession: boolean;
  message: string | null | undefined;
  fetchSessionInfo: () => void;
  logoutUser: () => void;
};

const recordLocationThenRedirectToLogin = (message?: string | null) => {
  // We are writing to the history api to record the location
  // then triggering a hard refresh
  let { location, history } = window

  if (location.pathname !== LOGIN_URL) {
    if (!history.pushState) {
      // pushState has a lot of support, but in case it doesn't...
      // we'll do a crude redirect
// @ts-expect-error - TS2322 - Type 'string' is not assignable to type 'Location'.
      location = LOGIN_URL
    } else {
      history.pushState({
        state: {
          redirect: getRedirect(location),
          message: message || null,
        }
      }, 'Login', LOGIN_URL)

      // Need to trigger a reload (as our url is now set to LOGIN_URL)
      // as mutating the window location will cause another entry in the history api
// @ts-expect-error - TS2554 - Expected 0 arguments, but got 1.
      location.reload(true)
    }
  }

  return null
}

const PrivateRoute = ({
  children,
  user,
  features,
  authorization,
  canSwitchOrganisations,
  organisations,
  currentlyEnrolled,
  participantTypes,
  submitting,
  clientSettings,
  loadingSession,
  message,
  fetchSessionInfo,
  logoutUser,
}: Props) => {
  const isFirstRender = useRef(true)
  const hasRedirected = useRef(false)

  const isAuthenticated = Boolean(user?.id)

  useEffect(function hydrateSessionOnMount() {
    if (!isAuthenticated) {
      fetchSessionInfo()
    }
    // Mark first render as completed
    isFirstRender.current = false
  }, [])

  // --- Redirect logic ---
  // On first render and there is no user: DO NOT redirect — allow fetch session saga to start
  if (!isAuthenticated && isFirstRender.current) {
    return null
  }

  // If there is no user yet and saga is loading the session: DO NOT redirect yet
  if (!isAuthenticated && loadingSession) {
    return null
  }

  // After saga resolved: redirect if there is still no user
  if (!isAuthenticated) {
    if (!hasRedirected.current) {
      recordLocationThenRedirectToLogin(message)
      hasRedirected.current = true
    }
    return null
  }

  const isFeatureActive = (feature: string): boolean => _isFeatureActive({ features, feature })
  const canMakeInternalSubmission = (): boolean => {
    return Permissions.isOperationPermitted(
      authorization,
      API_ROUND_ENROLMENTS_INTERNAL_SUBMISSIONS_CREATE,
    )
  }
  const organisationIds = organisations.map(({ id }) => id).toString()

  return (
    <AuthProvider
      value={{
        organisationIds,
        features,
        user,
        isFeatureActive,
        authorization,
        logoutUser,
        canMakeInternalSubmission: canMakeInternalSubmission(),
        canSwitchOrganisations,
        selectedOrganisations: organisations,
        fetchSessionInfo,
        canViewAnalytics: currentlyEnrolled,
        participantTypes,
        submitting,
        clientSettings,
      }}
    >
      <div key={organisationIds}>{children}</div>
    </AuthProvider>
  )
}

const mapStateToProps = (state: any) => {
  const {
    auth: {
      user,
      features,
      canSwitchOrganisations,
      organisations,
      currentlyEnrolled,
      participantTypes,
      submitting,
      clientSettings,
      loadingSession,
      message,
    },
  } = state
  const authorization = authorizationSelector(state)

  return {
    user,
    features,
    authorization,
    canSwitchOrganisations,
    organisations,
    currentlyEnrolled,
    participantTypes,
    submitting,
    clientSettings,
    loadingSession,
    message,
  }
}

const mapDispatchToProps = {
  fetchSessionInfo: authActions.fetchSessionInfo,
  logoutUser: authActions.logoutUser,
} as const

export default connect(mapStateToProps, mapDispatchToProps)(PrivateRoute)
